import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Movie } from '../models';

@Component({
  selector: 'app-compact-movie-row',
  standalone: true,
  imports: [CommonModule, RouterLink],
  template: `
    <a class="compact-row movie-row" *ngIf="movie" [routerLink]="['/movies', movie.id]" [queryParams]="detailQueryParams()">
      <span class="thumb">
        <img *ngIf="movie.posterUrl" [src]="movie.posterUrl" [alt]="movie.title + ' poster'" loading="lazy" />
        <span *ngIf="!movie.posterUrl">{{ initials(movie.title) }}</span>
      </span>
      <span class="row-main">
        <strong>{{ movie.title }}</strong>
        <small>{{ detailText() }}</small>
      </span>
      <span class="row-action">Times</span>
    </a>
  `,
  styles: [
    `
      .compact-row {
        min-height: 74px;
        padding: 8px;
        border: 1px solid var(--line);
        border-radius: 16px;
        background: var(--surface-row);
        box-shadow: 0 10px 28px rgba(0,0,0,.16);
        display: grid;
        grid-template-columns: 52px minmax(0, 1fr) auto;
        gap: 11px;
        align-items: center;
      }

      .thumb {
        width: 52px;
        height: 58px;
        border-radius: 12px;
        overflow: hidden;
        display: grid;
        place-items: center;
        color: var(--text);
        background: linear-gradient(135deg, var(--blue), var(--violet));
        font-weight: 950;
      }

      .thumb img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .row-main {
        min-width: 0;
        display: grid;
        gap: 5px;
      }

      .row-main strong {
        color: var(--text);
        font-size: 15px;
        line-height: 1.15;
        overflow-wrap: anywhere;
      }

      .row-main small {
        color: var(--muted);
        font-size: 12px;
        font-weight: 800;
      }

      .row-action {
        min-height: 40px;
        border-radius: 12px;
        padding: 0 12px;
        display: grid;
        place-items: center;
        color: var(--accent-contrast);
        background: var(--cyan);
        font-size: 13px;
        font-weight: 950;
      }

      @media (max-width: 420px) {
        .compact-row {
          grid-template-columns: 48px minmax(0, 1fr) 58px;
          gap: 8px;
          min-height: 70px;
        }

        .thumb {
          width: 48px;
          height: 54px;
        }

        .row-action {
          padding: 0 8px;
        }
      }
    `,
  ],
})
export class CompactMovieRowComponent {
  @Input({ required: true }) movie!: Movie;

  constructor(private readonly route: ActivatedRoute) {}

  detailQueryParams(): Record<string, string> {
    const params = this.route.snapshot.queryParamMap;
    return Object.fromEntries(
      ['date', 'county', 'language']
        .map((key) => [key, params.get(key)] as const)
        .filter((entry): entry is readonly [string, string] => Boolean(entry[1])),
    );
  }

  detailText(): string {
    return this.movie.language || (this.movie.runtimeMinutes ? `${this.movie.runtimeMinutes} min` : 'Live showtimes');
  }

  initials(title: string): string {
    return title.split(/\s+/).slice(0, 2).map((word) => word[0]).join('').toUpperCase();
  }
}
